import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { next } from '@ember/runloop';
import EmberObject, { action, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { A } from '@ember/array';
import { Theme } from 'mewe/constants';
import GroupStore from 'mewe/stores/group-store';
import ChatStore from 'mewe/stores/chat-store';
import PostApi from 'mewe/api/post-api';
import { toSimpleUser, toSimpleGroup } from 'mewe/fetchers/utils';
import { toPost } from 'mewe/stores/models/post-model';

export default class Dialogs extends Component {
  @service dynamicDialogs;
  @service purchase;
  @service account;
  @service tasks;

  @tracked dialogs = A();
  @tracked currentDialogIndex = 0;
  @tracked currentVariantIndex = 0;
  @tracked lastOpenedDialogName = null;

  constructor() {
    super(...arguments);

    // Dialog configuration array
    this.dialogConfigs = [
      { name: 'about-badges-dialog', getParamsFn:(v) => this.getBadgesInfoDialogParams(v)},
      { name: 'about-profile-dialog', getParamsFn: () => {}},
      { name: 'add-to-album-dialog', getParamsFn: (v) => this.getAddToAlbumDialogParams(v), variants: ['add', 'remove']},
      { name: 'bill-of-rights-dialog', getParamsFn: () => {}},
      { name: 'browse-communities-dialog', getParamsFn: (v) => this.getBrowseCommunitiesDialogParams(v), variants: [Theme.GROUP, Theme.PAGE]},
      { name: 'confirm-deletion-dialog', getParamsFn:(v) => this.getConfirmDeletionDialogParams(v)},
      { name: 'create-event-dialog', getParamsFn:(v) => this.getEventCreateDialogParams(v)},
      { name: 'create-folder-dialog', getParamsFn:(v) => this.getCreateFolderDialogParams(v)},
      { name: 'create-group-dialog', getParamsFn:(v) => this.getGroupCreateDialogParams(v)},
      { name: 'create-page-dialog', getParamsFn:(v) => this.getCreatePageDialogParams(v)},
      { name: 'credit-card-info-dialog', getParamsFn:(v) => this.getCreditCardInfoDialogParams(v)},
      { name: 'crowdscore-dialog', getParamsFn:(v) => this.getCrowdscoreDialogParams(v)},
      { name: 'debug-tool-dialog', getParamsFn:(v) => this.getCodeVersionDialogParams(v), disabled: true },
      { name: 'delete-account-dialog', getParamsFn:(v) => this.getDeleteAccountDialogParams(v)},
      { name: 'did-you-know-dialog', getParamsFn:(v) => this.getDidYouKnowDialogParams(v)},
      { name: 'discover-interests-dialog', getParamsFn:(v) => this.getDiscoverInterestsDialogParams(v)},
      { name: 'do-you-know-dialog', getParamsFn:(v) => this.getDoYouKnowDialogParams(v)},
      { name: 'download-content-dialog', getParamsFn:(v) => this.getDownloadContentDialogParams(v)},
      { name: 'edit-album-dialog', getParamsFn:(v) => this.getEditAlbumDialogParams(v)},
      { name: 'emoji-senders-dialog', getParamsFn:(v) => this.getEmojiSendersDialogParams(v)},
      { name: 'event-owner-conflict-dialog', getParamsFn:(v) => this.getEventOwnerConflictDialogParams(v)},
      { name: 'event-repeat-dialog', getParamsFn:(v) => this.getEventRepeatDialogParams(v)},
      { name: 'exported-content-dialog', getParamsFn:(v) => this.getExportedContentDialogParams(v)},
      { name: 'feedback-dialog', getParamsFn:(v) => this.getFeedbackDialogParams(v)},
      { name: 'followers-dialog', getParamsFn:(v) => this.getFollowersDialogParams(v)},
      { name: 'group-add-user-dialog', getParamsFn:(v) => this.getGroupInviteDialogParams(v)},
      { name: 'group-conflict-admin-dialog', getParamsFn:(v) => this.getGroupAdminConflictDialogParams(v)},
      { name: 'group-conflict-owner-dialog', getParamsFn:(v) => this.getGroupOwnerConflictDialogParams(v)},
      { name: 'group-apply-dialog', getParamsFn:(v) => this.getGroupApplyDialogParams(v)},
      { name: 'group-blocking-confirm-dialog', getParamsFn:(v) => this.getGroupBlockingConfirmDialogParams(v)},
      { name: 'group-initialisation-dialog', getParamsFn:(v) => this.getGroupInitialisationDialogParams(v)},
      { name: 'group-joined-dialog', getParamsFn:(v) => this.getGroupJoinedDialogParams(v)},
      { name: 'group-preview-dialog', getParamsFn:(v) => this.getGroupPreviewDialogParams(v), disabled: true },
      { name: 'incoming-call-dialog', getParamsFn:(v) => this.getIncomingCallDialogParams(v)},
      { name: 'invitations-dialog', getParamsFn:(v) => this.getInvitationsDialogParams(v), variants: ['contact invitation', 'group invitation']}, // Requires contact invitation data from server
      { name: 'media-dialog', getParamsFn:(v) => this.getMediaDialogParams(v), variants: [], disabled: true }, // Requires complex post/media data from server
      { name: 'move-file-dialog', getParamsFn:(v) => this.getMoveFileDialogParams(v)},
      { name: 'mutual-contacts-dialog', getParamsFn:(v) => this.getMutualContactsDialogParams(v)},
      { name: 'new-to-mewe-dialog', getParamsFn:(v) => this.getNewToMeweDialogParams(v)},
      { name: 'new-tos-dialog', getParamsFn:(v) => this.getNewTosDialogParams(v)},
      { name: 'onboarding-dialog', getParamsFn:(v) => {}},
      { name: 'pick-chat-dialog', getParamsFn:(v) => this.getChatCandidatesDialogParams(v)},
      { name: 'pick-photo-dialog', getParamsFn:(v) => this.getChoosePhotoDialogParams(v)},
      { name: 'pick-user-dialog', getParamsFn:(v) => this.getUserPickerDialogParams(v)},
      { name: 'postbox-dialog', getParamsFn:(v) => this.getPostboxDialogParams(v)},
      { name: 'premium-feature-dialog', getParamsFn:(v) => this.getPremiumFeatureDialogParams(v)},
      { name: 'quick-share-profile-dialog', getParamsFn:(v) => this.getQuickShareProfileDialogParams(v)},
      { name: 'remove-dialog', getParamsFn:(v) => this.getRemoveDialogParams(v)},
      { name: 'remove-folder-dialog', getParamsFn:(v) => this.getRemoveFolderDialogParams(v)},
      { name: 'rename-audio-file-dialog', getParamsFn:(v) => this.getRenameAudioFileDialogParams(v)},
      { name: 'report-dialog', getParamsFn:(v) => this.getReportDialogParams(v), variants: ['contact', 'post', 'group']},
      { name: 'reputation-info-dialog', getParamsFn:(v) => this.getReputationInfoDialogParams(v)},
      { name: 'share-mewe-dialog', getParamsFn:(v) => this.getShareMeweDialogParams(v)},
      { name: 'simple-dialog-new', getParamsFn:(v) => this.getSimpleDialogNewParams(v)},
      { name: 'single-post-dialog', getParamsFn:(v) => this.getSinglePostDialogParams(v), variants: ['text', 'photo']}, // Requires complex post data from server
      { name: 'stories-dialog', getParamsFn:(v) => this.getStoriesDialogParams(v), disabled: true }, // Requires complex story/user data from server
      { name: 'store-cancel-subscription-dialog', getParamsFn:(v) => this.getStoreCancelSubscriptionDialogParams(v)},
      { name: 'store-checkout-dialog', getParamsFn:(v) => this.getStoreCheckoutDialogParams(v)},
      { name: 'store-donor-badge-dialog', getParamsFn:(v) => this.getStoreDonorBadgeDialogParams(v)},
      { name: 'store-fail-dialog', getParamsFn:(v) => this.getStoreFailDialogParams(v)},
      { name: 'store-item-donation-dialog', getParamsFn:(v) => this.getStoreItemDonationDialogParams(v)},
      { name: 'store-item-page-dialog', getParamsFn:(v) => this.getStoreItemPageDialogParams(v)},
      { name: 'store-item-preview-dialog', getParamsFn:(v) => this.getStoreItemPreviewDialogParams(v)},
      { name: 'store-item-premium-dialog', getParamsFn:(v) => this.getStoreItemPremiumDialogParams(v), variants: ['ftue', 'regular']},
      { name: 'store-item-storage-dialog', getParamsFn:(v) => this.getStoreItemStorageDialogParams(v)},
      { name: 'store-success-dialog', getParamsFn:(v) => this.getStoreSuccessDialogParams(v), variants: ['page', 'emoji', 'stickers']},
      { name: 'store-success-premium-dialog', getParamsFn:(v) => this.getStoreSuccessPremiumDialogParams(v)},
      { name: 'store-success-storage-dialog', getParamsFn:(v) => this.getStoreSuccessStorageDialogParams(v)},
      { name: 'story-editor-dialog', getParamsFn:(v) => this.getStoryEditorDialogParams(v)},
      { name: 'timezone-dialog', getParamsFn:(v) => this.getTimezoneDialogParams(v)},
      { name: 'translation-dialog', getParamsFn:(v) => this.getTranslationDialogParams(v)},
      { name: 'trial-reminder-dialog', getParamsFn:(v) => this.getStoreTrialReminderDialogParams(v)},
      { name: 'wrappers-dialog', getParamsFn:(v) => this.getWrappersDialogParams(v)},
    ];

    next(() => {
      this.dialogConfigs.forEach(config => {
        const dialog = EmberObject.create({
          name: config.name,
          variants: config.variants || [],
          disabled: config.disabled || false,
          isExpanded: false,
          openFn: (variant) => {
            if (!config.disabled) {
              this.openDialogWithParams(config.name, ( ) => config.getParamsFn?.(variant), variant);
            }
          },
        });
        this.dialogs.pushObject(dialog);
      });

      // Add keyboard event listeners for navigation
      this.addKeyboardListeners();
    });
  }

  // Core method to open dialog with async params
  async openDialogWithParams(dialogName, getParamsFn, variant = null) {
    let params = {};
    if (typeof getParamsFn === 'function') {
      params = await getParamsFn(variant);
    }
    this.dynamicDialogs.openDialog(dialogName, params);

    // Track the last opened dialog
    this.lastOpenedDialogName = dialogName;

    // Update current dialog index - find in the actual dialogs array, not dialogConfigs
    this.currentDialogIndex = this.dialogs.findIndex(dialog => dialog.name === dialogName);

    // Set variant index based on the variant that was opened
    if (variant) {
      const dialog = this.getCurrentDialog();
      if (dialog && dialog.variants.length > 0) {
        this.currentVariantIndex = dialog.variants.indexOf(variant);
        console.log('Opened dialog with variant:', variant, 'variant index:', this.currentVariantIndex);
      }
    } else {
      // Reset variant index for the opened dialog
      this.currentVariantIndex = 0;
    }
  }

  formatDialogName(dialogName) {
    // Convert kebab-case to Title Case for display
    return dialogName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  @action
  itemClick(dialog) {
    // Don't allow clicking on disabled dialogs
    if (dialog.disabled) {
      return;
    }

    if (dialog.variants.length) {
      set(dialog, 'isExpanded', !dialog.isExpanded);
    } else {
      dialog.openFn();
    }
  }

  // Navigation methods
  @action
  nextDialog() {
    if (this.dynamicDialogs.hasAnyDialogOpened) {
      this.dynamicDialogs.closeAll();
    }

    const currentDialog = this.getCurrentDialog();

    // If current dialog has variants and we're not at the last variant
    if (currentDialog && currentDialog.variants.length > 0 && this.currentVariantIndex < currentDialog.variants.length - 1) {
      this.currentVariantIndex++;
      this.openCurrentDialog();
    }
    // If current dialog has no variants or we're at the last variant, move to next enabled dialog
    else {
      let nextIndex = this.currentDialogIndex + 1;

      // Find next enabled dialog
      while (nextIndex < this.dialogs.length && this.dialogs[nextIndex].disabled) {
        nextIndex++;
      }

      if (nextIndex < this.dialogs.length) {
        this.currentDialogIndex = nextIndex;
        this.currentVariantIndex = 0; // Reset variant index for new dialog
        this.openCurrentDialog();
      }
    }
  }

  @action
  previousDialog() {
    // Close any open dialogs first
    if (this.dynamicDialogs.hasAnyDialogOpened) {
      this.dynamicDialogs.closeAll();
    }

    const currentDialog = this.getCurrentDialog();

    // If current dialog has variants and we're not at the first variant
    if (currentDialog && currentDialog.variants.length > 0 && this.currentVariantIndex > 0) {
      this.currentVariantIndex--;
      this.openCurrentDialog();
    }
    // If current dialog has no variants or we're at the first variant, move to previous enabled dialog
    else {
      let prevIndex = this.currentDialogIndex - 1;

      // Find previous enabled dialog
      while (prevIndex >= 0 && this.dialogs[prevIndex].disabled) {
        prevIndex--;
      }

      if (prevIndex >= 0) {
        this.currentDialogIndex = prevIndex;
        const prevDialog = this.getCurrentDialog();
        // Set variant index to last variant of previous dialog if it has variants
        this.currentVariantIndex = prevDialog && prevDialog.variants.length > 0 ? prevDialog.variants.length - 1 : 0;
        this.openCurrentDialog();
      }
    }
  }

  openCurrentDialog() {
    const currentDialog = this.dialogs[this.currentDialogIndex];
    if (currentDialog) {
      // If dialog has variants, open with current variant
      if (currentDialog.variants.length > 0) {
        const variant = currentDialog.variants[this.currentVariantIndex];
        currentDialog.openFn(variant);
      } else {
        currentDialog.openFn();
      }
    }
  }

  getCurrentDialog() {
    return this.dialogs[this.currentDialogIndex];
  }

  getCurrentVariant() {
    const currentDialog = this.getCurrentDialog();
    if (currentDialog && currentDialog.variants.length > 0) {
      return currentDialog.variants[this.currentVariantIndex];
    }
    return null;
  }

  getTotalNavigationItems() {
    let total = 0;
    this.dialogs.forEach(dialog => {
      if (dialog.variants.length > 0) {
        total += dialog.variants.length;
      } else {
        total += 1;
      }
    });
    return total;
  }

  getCurrentNavigationPosition() {
    let position = 0;
    for (let i = 0; i < this.currentDialogIndex; i++) {
      const dialog = this.dialogs[i];
      if (dialog.variants.length > 0) {
        position += dialog.variants.length;
      } else {
        position += 1;
      }
    }
    position += this.currentVariantIndex + 1;
    return position;
  }

  getCurrentDialogDisplayName() {
    const currentDialog = this.getCurrentDialog();
    if (!currentDialog) return '';

    const baseName = this.formatDialogName(currentDialog.name);
    const variant = this.getCurrentVariant();

    if (variant) {
      return `${baseName} (${variant})`;
    }
    return baseName;
  }

  getNavigationInfo() {
    const currentPosition = this.getCurrentNavigationPosition();
    const totalItems = this.getTotalNavigationItems();
    const currentDialog = this.getCurrentDialog();
    const currentVariant = this.getCurrentVariant();

    return {
      currentPosition,
      totalItems,
      dialogName: currentDialog ? currentDialog.name : '',
      variant: currentVariant,
      hasVariants: currentDialog ? currentDialog.variants.length > 0 : false,
      variantIndex: this.currentVariantIndex,
      totalVariants: currentDialog ? currentDialog.variants.length : 0
    };
  }

  addKeyboardListeners() {
    // Store reference to the handler for cleanup
    this.handleKeydown = (event) => {
      // Only handle arrow keys when no input/textarea is focused
      if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') {
        return;
      }

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        this.nextDialog();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.previousDialog();
      }
    };

    // Add event listener for keyboard navigation
    document.addEventListener('keydown', this.handleKeydown);
  }

  willDestroy() {
    super.willDestroy();
    // Clean up keyboard event listeners
    if (this.handleKeydown) {
      document.removeEventListener('keydown', this.handleKeydown);
    }
  }

  async getAddToAlbumDialogParams(variant) {
    if (variant === 'add') {
      return {
        post: {
          id: 'mock-post-1',
          album: null,
          group: { id: 'contacts' },
          mediasCount: 1,
        },
        groupId: 'contacts',
        theme: 'contacts'
      };
    } else {
      return {
        post: {
          id: 'mock-post-1',
          album: 'test-album',
          group: { id: 'contacts' },
        },
        groupId: 'contacts',
        theme: 'contacts'
      };
    }
  }

  async getBadgesInfoDialogParams() {
    // Mock user data with all badge types for demo
    return {
      user: {
        id: 'mock-user-1',
        name: 'Demo User',
        badges: {
          verified: true,
          premium: true,
          donor: true,
          ambassador: true,
          level4: true,
          level3: false,
          level2: false,
          level1: false
        }
      }
    };
  }

  async getBrowseCommunitiesDialogParams(variant = 'groups') {
    return { theme: variant }
  }

  async getChatCandidatesDialogParams() {
    return {
      saveText: __('Forward Message'),
      submitAction: (users, groups, threads) => {
        console.log('Users, groups, threads:', users, groups, threads);
      },
    };
  }

  async getChoosePhotoDialogParams() {
    const coverTask = this.tasks.getTask('photo.cover.contacts')
    const selectPhoto = (blob, params) => {
      dispatcher.dispatch('contact', 'setPublicCover', blob, params, coverTask);
    }
    return {
      theme: 'cover-profile',
      url: '/api/v2/photo/uc',
      task: coverTask,
      selectPhoto: selectPhoto,
      titles: {
        choosePhoto: __('Choose Background Photo'),
        cropPhoto: __('Edit Background Photo'),
      },
    }
  }

  async getCodeVersionDialogParams() {
    return {};
  }

  async getConfirmDeletionDialogParams() {
    // Mock confirmation dialog for deletion
    return {
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this item? This action cannot be undone.',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      isDangerous: true
    };
  }

  async getCreateFolderDialogParams() {
    return {
      rootId: 'root',
      groupId: Theme.MYCLOUD,
      groupName: __('My Cloud'),
    };
  }

  async getCreatePageDialogParams() {
    return {};
  }

  async getCreditCardInfoDialogParams() {
    return {};
  }

  async getCrowdscoreDialogParams() {
    return {};
  }

  async getDeleteAccountDialogParams() {
    return {};
  }

  async getDidYouKnowDialogParams() {
    // Mock tip data for did you know dialog
    return {
      tipName: 'contactRequest' // Can be 'contactRequest' or 'chatRequest'
    };
  }

  async getDiscoverInterestsDialogParams() {
    return {};
  }

  async getDoYouKnowDialogParams() {
    return {
      image: '/assets/images/do-you-know.webp',
      okButtonText: __('Got it!'),
      message: __(
        'Private profile cannot be followed by more than 8000 people. Please change your profile to public to keep your current followers, or create a page or a group to manage your large communities.'
      ),
      noEscaping: true,
    };
  }

  async getDownloadContentDialogParams() {
    return {};
  }

  async getEditAlbumDialogParams() {
    return {
      scope: 'mycloud',
      album: { name: 'stories' },
      hasFullAlbumRights: true,
    };
  }

  async getEmojiSendersDialogParams() {
    return {
      api: PostApi,
      model: {
        postItemId: '623dbaa3a478c015a5c250d2',
        groupId: this.universalGroupId,
        emojisItems: this.emojisItems,
       },
      scope: Theme.GROUP,
      isAllTab: true,
    };
  }

  async getEventCreateDialogParams() {
    return {};
  }

  async getEventOwnerConflictDialogParams() {
    return {
      user: this.account.activeUser,
      eventId: '123',
      blockedCount: 10,
    };
  }

  async getEventRepeatDialogParams() {
    return {
      repeat: { repeatValue: 0 },
      startDate: new Date().getTime(),
      endDate: new Date().getTime() + 1000 * 60 * 60 * 24 * 30,
      onOkClick: () => {},
    };
  }

  async getExportedContentDialogParams() {
    return {};
  }

  async getFeedbackDialogParams() {
    return {};
  }

  async getFollowersDialogParams() {
    return {
      uuid: this.account.activeUser.id,
      currentTab: 'followers',
      title: this.account.activeUser.name,
      isPublicProfile: this.account.activeUser.public,
      contactId: this.account.activeUser.id,
    };
  }

  async getGroupAdminConflictDialogParams() {
    return {
      member: this.account.activeUser,
      groupId: '123',
    };
  }

  async getGroupApplyDialogParams() {
    return { group: this.getGroup() };
  }

  async getGroupBlockingConfirmDialogParams() {
    return {
      userId: this.account.activeUser.id,
      userName: "John Doe",
      currentGroupId: '123',
    };
  }

  async getGroupCreateDialogParams() {
    return {};
  }

  async getGroupInitialisationDialogParams() {
    return { group: this.getGroup() };
  }

  async getGroupInviteDialogParams() {
    return {
      inviteeId: this.account.activeUser.id,
      inviteeName: this.account.activeUser.firstName,
    };
  }

  async getGroupJoinedDialogParams() {
    return { group: this.getGroup() };
  }

  async getGroupOwnerConflictDialogParams() {
    return {
      member: this.account.activeUser,
      groupId: this.getGroup().id,
      blockedCount: 10,
    };
  }

  async getGroupPreviewDialogParams() {
    return { groupFromParent: this.getGroup() };
  }

  async getIncomingCallDialogParams() {
    return {
      thread: this.getThread(),
      video: true,
    };
  }

  async getInvitationsDialogParams(variant) {
    if (variant === 'contact invitation') return { isContactInvitation: true };
    if (variant === 'group invitation') return { group: this.getGroup() };
    return {};
  }

  async getMediaDialogParams() {
    // Example: Media dialog might need media items
    return {
      // mediaItems: this.selectedMediaItems,
      // allowMultiple: true,
      // context: 'gallery_view'
    };
  }

  async getMoveFileDialogParams() {
    return {
      group: { id: "mycloud", name: "My Cloud" },
      document: { name: 'report.pdf', id: '123', groupId: "mycloud", isFolder: false },
    };
  }

  async getMutualContactsDialogParams() {
    return {
      contacts: [this.getUserA(), this.getUserB(), this.getUserC()],
      totalCount: 3,
      theme: Theme.GROUP,
      groupId: this.getGroup().id,
    };
  }

  async getNewToMeweDialogParams() {
    return {};
  }

  async getNewTosDialogParams() {
    return {};
  }

  async getPostboxDialogParams() {
    return {
      theme: Theme.CONTACTS,
      target: Theme.CONTACTS,
    };
  }

  async getPremiumFeatureDialogParams() {
    return {};
  }

  async getQuickShareProfileDialogParams() {
    return {
      user: this.account.activeUser,
    };
  }

  async getRemoveDialogParams() {
    return { post: this.getPost() };
  }

  async getRemoveFolderDialogParams() {
    return { folder: { name: 'report' } };
  }

  async getRenameAudioFileDialogParams() {
    return { audio: { name: 'report.mp3' } };
  }

  async getReportDialogParams(variant) {
    if (variant === 'contact') return { contact: this.getUserA() };
    if (variant === 'post') return { post: this.getPost() };
    if (variant === 'group') return { group: this.getGroup() };
    return {};
  }

  async getReputationInfoDialogParams() {
    return {
      title: __('Vote limit reached'),
      description: __('You have up to 10 votes for each user (negative or positive)'),
    };
  }

  async getShareMeweDialogParams() {
    return {};
  }

  async getSimpleDialogNewParams() {
    // Simple dialog with demo content
    return {
      title: 'Demo Dialog',
      message: 'This is a demonstration of the simple dialog component. You can customize the title, message, and button text.',
      okButtonText: 'Got it!',
      cancelButtonText: 'Cancel'
    };
  }

  async getSinglePostDialogParams(variant) {
    if (variant === 'text') return { post: this.getPost(), scope: Theme.GROUP };
    if (variant === 'photo') return { post: this.getPostPhoto(), scope: Theme.CONTACTS };
  }

  async getStoriesDialogParams() {
    return {};
  }

  async getStoryEditorDialogParams() {
    return {
      imgUrl: '/api/v2/photo/TlH0svMdmhuRQejxzkaK7O9uDoVZREiXVgahPC5yqo9nSXYxQyYoBsS620o/{imageSize}/img?static={static}',
    };
  }

  async getTimezoneDialogParams() {
    return { selectedZoneId: 'America/New_York' };
  }

  async getTranslationDialogParams() {
    return { isTranslated: false };
  }

  async getUserPickerDialogParams() {
    return {
      pickerType: 'addToChat',
      selectedUsers: [],
    };
  }

  async getWrappersDialogParams() {
    return {
      wrapperId: this.getGroup().id,
      wrappersScope: Theme.GROUP,
    };
  }

  // Store dialog params
  async getStoreCancelSubscriptionDialogParams() {
    return { endDate: 'May 15, 2025' };
  }

  async getStoreCheckoutDialogParams() {
    const item = await this.purchase.getItemPromise('page');
    return {
      itemsToPurchase: [item],
    };
  }

  async getStoreDonorBadgeDialogParams() {
    return {};
  }

  async getStoreFailDialogParams() {
    return {};
  }

  async getStoreItemDonationDialogParams() {
    return {};
  }

  async getStoreItemPageDialogParams() {
    return {};
  }

  async getStoreItemPreviewDialogParams() {
    return { itemId: 'emoji-blobs' };
  }

  async getStoreItemPremiumDialogParams(variant) {
    if (variant === 'ftue') {
      return { isFtueFlow: true };
    }
    return {};
  }

  async getStoreItemStorageDialogParams() {
    return {};
  }

  async getStoreSuccessDialogParams(variant) {
    if (variant === 'page') return { pageSubscription: true };
    if (variant === 'emoji') return { emojiPackPurchased: true };
    if (variant === 'stickers') return { stickersPurchased: true };
  }

  async getStoreSuccessPremiumDialogParams() {
    return {};
  }

  async getStoreSuccessStorageDialogParams() {
    return {};
  }

  async getStoreTrialReminderDialogParams() {
    return { provider: 'paypal', productId: 'premium' };
  }

  // Method to set browse-communities-dialog to page variant for testing
  @action
  setBrowseCommunitiesToPageVariant() {
    const browseDialogIndex = this.dialogs.findIndex(d => d.name === 'browse-communities-dialog');
    if (browseDialogIndex !== -1) {
      this.setCurrentDialog(browseDialogIndex, 1); // Set to 'page' variant
    }
  }

  // utils
  universalGroupId = '607ebdcee8560e056ca251ec';

  emojisItems = [
    {
        "unicode": ":peace-sign_chinese-flag:",
        "counter": 13,
        "index": 1,
        "html": "<span data-name=\"peace-sign_chinese-flag\" class=\"emoji\" style=\"background-image: url(/cdn/emoji/peace-sign/sprites.1pea9.png); background-position: -16px -16px; background-size: 112px 96px;\" ></span>",
        "isUser": false
    },
    {
        "unicode": ":blobs_bunny:",
        "counter": 16,
        "index": 2,
        "html": "<span data-name=\"blobs_bunny\" class=\"emoji\" style=\"background-image: url(/cdn/emoji/blobs/sprites.1lob9.png); background-position: -0px -0px; background-size: 64px 64px;\" ></span>",
        "isUser": false
    },
    {
        "unicode": "🦜",
        "counter": 14,
        "index": 3,
        "html": "<span data-name=\"parrot\" class=\"emoji\" style=\"background-image: url(/cdn/emoji/default/sprites-3.1defa3.png); background-position: -128px -160px; background-size: 304px 304px;\" >🦜</span>",
        "isUser": false
    },
    {
        "unicode": "🧸",
        "counter": 11,
        "index": 4,
        "html": "<span data-name=\"teddy_bear\" class=\"emoji\" style=\"background-image: url(/cdn/emoji/default/sprites-3.1defa3.png); background-position: -32px -0px; background-size: 304px 304px;\" >🧸</span>",
        "isUser": false
    }
  ];

  getGroup() {
    const group = GroupStore.getState().confirmedGroups?.[0] || { id: '123' };
    group.applyQuestions = [
      { question: "What is your favorite color?" },
      { question: "What is your favorite animal?" }
    ];
    return group;
  }

  getPost() {
    const post = toPost({
      "postItemId": "68a72dadfce17546954f97b9",
      "userId": "6065915460e3b24b2bfa4d2a",
      "text": "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
      "permissions": {
        "comment": true,
        "remove": true,
        "edit": true,
        "canFeature": true,
        "canEmojify": true,
        "repost": true,
        "reshare": true,
        "follow": true,
        "canAddEmoji": true
      },
      "createdAt": 1755786669,
      "updatedAt": 1755786669,
      "editedAt": 1755788096,
      "follows": true,
      "public": true,
      "sharesCount": 1,
      "language": "en"
    });
    post.owner = this.getUserA();
    return post;
  }

  getPostPhoto() {
    const post = toPost({
      "postItemId": "6749763343262806c4badb4d",
      "userId": "6065915460e3b24b2bfa4d2a",
      "text": "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
      "medias": [
        {
          "mediaId": "6749763343262806c4badb4c",
          "postItemId": "6749763343262806c4badb4f",
          "photo": {
            "id": "6749763143262806c4badb4a",
            "size": {
              "width": 1024,
              "height": 754
            },
            "mime": "image/jpeg",
            "_links": {
              "img": {
                "href": "/api/v2/photo/TlH0svMdmhuRQejxzkaK7O9uDoVZREiXVgahPC5yqo9nSXYxQyYoBsS620o/{imageSize}/img?static={static}",
                "templated": true
              }
            }
          }
        },
        {
          "mediaId": "6749763343262806c4badb4b",
          "postItemId": "6749763343262806c4badb50",
          "photo": {
            "id": "6749763121892b0d72cbfb43",
            "size": {
              "width": 1334,
              "height": 1334
            },
            "mime": "image/jpeg",
            "_links": {
              "img": {
                "href": "/api/v2/photo/h2ASx4UjIkUmbU9DUgv_SKn0wtvqIXqwyH2-bf9jk21nSXYxIYkrDXLL-0M/{imageSize}/img?static={static}",
                "templated": true
              }
            }
          }
        }
      ],
      "mediasCount": 2,
      "photosCount": 2,
      "permissions": {
        "comment": true,
        "remove": true,
        "edit": true,
        "canFeature": true,
        "canEmojify": true,
        "repost": true,
        "reshare": true,
        "follow": true,
        "canAddEmoji": true
      },
      "createdAt": 1732867635,
      "updatedAt": 1732867635,
      "follows": true,
      "public": true
    });
    post.owner = this.getUserA();
    return post;
  }

  getUserA() {
    return toSimpleUser({
      "id": "6065915460e3b24b2bfa4d2a",
      "name": "Dominik MeWe",
      "_links": {
        "avatar": {
          "href": "/api/v2/photo/profile/{imageSize}/6065915460e3b24b2bfa4d2a?group=&f=F60258980131MI401E",
          "templated": true
        },
        "cover": {
          "href": "/api/v2/photo/cover/{imageSize}/6065915460e3b24b2bfa4d2a?f=F60258980131MI401E",
          "templated": true,
          "nsfw": false
        }
      },
      "fingerprint": "F60258980131MI401E",
      "firstName": "Dominik",
      "lastName": "MeWe",
      "badges": {
        "premium": true,
        "ambassador": true
      },
      "publicLinkId": "seki_ultimate242",
      "public": true
    });
  }

  getUserB() {
    return toSimpleUser({
      "id": "615ed82346b82e7a0c590591",
      "name": "Pawel P",
      "_links": {
        "avatar": {
          "href": "/api/v2/photo/profile/{imageSize}/615ed82346b82e7a0c590591?group=&f=F854449841123YBXQN5",
          "templated": true
        },
        "cover": {
          "href": "/api/v2/photo/cover/{imageSize}/615ed82346b82e7a0c590591?f=F854449841123YBXQN5",
          "templated": true
        }
      },
      "fingerprint": "F854449841123YBXQN5",
      "firstName": "Pawel",
      "lastName": "P",
      "contactInviteId": "pawelpalka",
      "badges": {
        "premium": true,
        "verified": true
      },
      "publicLinkId": "ppalka1.11",
      "public": true
    });
  }

  getUserC() {
    return toSimpleUser({
      "id": "608b4f472d3e7a049c78735c",
      "name": "Marcin Tester",
      "_links": {
        "avatar": {
          "href": "/api/v2/photo/profile/{imageSize}/608b4f472d3e7a049c78735c?group=&f=F101687070561615REJP",
          "templated": true
        },
        "cover": {
          "href": "/api/v2/photo/cover/{imageSize}/608b4f472d3e7a049c78735c?f=F101687070561615REJP",
          "templated": true,
          "nsfw": false
        }
      },
      "fingerprint": "F101687070561615REJP",
      "firstName": "Marcin",
      "lastName": "Tester",
      "contactInviteId": "marcinmackowiak",
      "badges": {
        "donor": true
      },
      "publicLinkId": "marcin.38",
      "public": false
    });
  }

  getThread() {
    const user = this.account.activeUser;
    user.id += 'x';

    return ChatStore.getState().threads?.[0] || { id: '123', participants: [user], getUserChatParticipant: ( ) => user };
  }
}
